import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogType?: string;
}

export default function SEO({ title, description, keywords, canonicalUrl, ogType = 'website' }: SEOProps) {
  const location = useLocation();
  const baseUrl = 'https://jmcomic.com';
  const fullUrl = canonicalUrl || `${baseUrl}${location.pathname}`;
  const fullTitle = title.includes('jm漫画') ? title : `${title} - jm漫画`;

  useEffect(() => {
    document.title = fullTitle;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', ogType, true);
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = fullUrl;

    const existingJsonLd = document.querySelectorAll('script[type="application/ld+json"]');
    existingJsonLd.forEach((el) => {
      if (el.textContent && el.textContent.includes('"@type":"WebPage"')) {
        el.remove();
      }
    });

    const webpageLd = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: fullTitle,
      description,
      url: fullUrl,
      isPartOf: {
        '@type': 'WebSite',
        name: 'jm漫画',
        url: baseUrl,
      },
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(webpageLd);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, [fullTitle, description, keywords, fullUrl, ogType]);

  return null;
}